import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HelpSupportButton } from "@/components/help-support";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  Target,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  AlertCircle,
  CheckCircle2,
  ArrowRightCircle,
  ArrowDownToLine,
  MoreVertical,
  Pin,
  PinOff,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  Settings,
  Info,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useReceiptsPageDataGraphQL, sortFieldMap, buildGraphQLFilterVariable } from "@/hooks/useReceiptsGraphQL";
import type { GetReceiptsParams } from "@/hooks/useReceiptsGraphQL";
import type { AllocationStatus } from "@/types/payments";
import type { ColumnInput } from "@/types/export";
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog";
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences";
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration";
import { useExportAll } from "@/hooks/useExportAll";
import { ExportProvider } from "@/providers/ExportProvider";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { getPageNumbers } from "@/lib/pagination-utils";
import { useCurrenciesList } from "@/hooks/useCurrenciesData";
import { useCompany } from "@/contexts/CompanyContext";
import { getCurrencyIcon } from '@/components/shared/currency-icon';

// ─── Local KPI Card ───────────────────────────────────────────────────────────

interface ReceiptsKPICardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: LucideIcon;
  colorScheme: "blue" | "green" | "amber" | "red";
  onClick?: () => void;
  isActive?: boolean;
  infoTooltip?: string;
}

const receiptKpiColors: Record<
  ReceiptsKPICardProps["colorScheme"],
  { border: string; iconBg: string; iconColor: string }
> = {
  blue:  { border: "border-l-blue-500",  iconBg: "#dbeafe", iconColor: "#2563eb" },
  green: { border: "border-l-green-500", iconBg: "#dcfce7", iconColor: "#16a34a" },
  amber: { border: "border-l-amber-500", iconBg: "#fef3c7", iconColor: "#d97706" },
  red:   { border: "border-l-red-500",   iconBg: "#fee2e2", iconColor: "#dc2626" },
};

function ReceiptsKPICard({ title, value, subtitle, icon: Icon, colorScheme, onClick, isActive, infoTooltip }: ReceiptsKPICardProps) {
  const colors = receiptKpiColors[colorScheme];
  return (
    <div
      onClick={onClick}
      className={cn(
        "relative rounded-lg border bg-white shadow-sm border-l-4 p-4 cursor-pointer transition-all hover:shadow-md",
        colors.border,
        isActive && "ring-2 ring-offset-1 ring-gray-300"
      )}
    >
      <div
        className="absolute top-3 right-3 p-2 rounded-full"
        style={{ backgroundColor: colors.iconBg }}
      >
        <Icon className="h-4 w-4" style={{ color: colors.iconColor }} />
      </div>
      <div className="pr-12">
        <div className="flex items-center gap-1">
          <p style={{ fontSize: 15, fontWeight: 400, color: "#111827" }}>{title}</p>
          {infoTooltip && (
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info
                    className="h-3.5 w-3.5 text-muted-foreground cursor-help flex-shrink-0"
                    aria-label={`${title} info`}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[220px] text-center">
                  {infoTooltip}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <p style={{ fontSize: 22, fontWeight: 600, color: "#111827" }} className="mt-1">{value}</p>
        <p style={{ fontSize: 12, fontWeight: 400, color: "#6b7280" }} className="mt-1">{subtitle}</p>
      </div>
    </div>
  );
}

// ─── Column Definitions ───────────────────────────────────────────────────────

const DEFAULT_RECEIPT_COLUMNS: ColumnDefinition[] = [
  { id: "receiptNumber",         label: "Receipt #",         visible: true,  order: 0  },
  { id: "customerAccountNumber", label: "Account Number",    visible: true,  order: 1  },
  { id: "customerName",          label: "Customer",          visible: true,  order: 2  },
  { id: "currency",              label: "Currency",          visible: true,  order: 3  },
  { id: "receiptDate",           label: "Receipt Date",      visible: true,  order: 4  },
  { id: "receivedAmount",        label: "Gross Amount",      visible: true,  order: 5  },
  { id: "allocatedAmount",       label: "Allocated Amount",  visible: false, order: 6  },
  { id: "unappliedAmount",       label: "Unallocated Amount",visible: true,  order: 7  },
  { id: "allocationStatus",      label: "Status",            visible: true,  order: 8  },
  { id: "createdBy",             label: "Created By",        visible: false, order: 9  },
  { id: "createdAt",             label: "Created At",        visible: false, order: 10 },
  { id: "modifiedBy",            label: "Modified By",       visible: false, order: 11 },
  { id: "modifiedAt",            label: "Modified At",       visible: false, order: 12 },
  { id: "actions",               label: "Actions",           visible: true,  order: 13 },
];

const COL_CONFIG: Record<string, { label: string; sortField?: string; align?: "right" }> = {
  receiptNumber:         { label: "Receipt #",          sortField: "receiptNumber" },
  customerAccountNumber: { label: "Account Number" },
  customerName:          { label: "Customer",           sortField: "customerName" },
  currency:              { label: "Currency" },
  receiptDate:           { label: "Receipt Date",       sortField: "receiptDate" },
  receivedAmount:        { label: "Gross Amount",       sortField: "receivedAmount", align: "right" },
  allocatedAmount:       { label: "Allocated Amount",   align: "right" },
  unappliedAmount:       { label: "Unallocated Amount", sortField: "unappliedAmount", align: "right" },
  allocationStatus:      { label: "Status",             sortField: "allocationStatus" },
  createdBy:             { label: "Created By" },
  createdAt:             { label: "Created At" },
  modifiedBy:            { label: "Modified By" },
  modifiedAt:            { label: "Modified At" },
  actions:               { label: "Actions",            align: "right" },
};

type SortField = "receiptNumber" | "customerName" | "receiptDate" | "receivedAmount" | "unappliedAmount" | "allocationStatus";
type SortDirection = "asc" | "desc" | null;

export default function ReceiptsPage() {
  return (
    <ExportProvider>
      <ReceiptsPageInner />
    </ExportProvider>
  );
}

function ReceiptsPageInner() {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const baseCurrency = selectedCompany?.currency || "GBP";
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get("search") ?? "");
  const debouncedSearch = useDebouncedValue(searchQuery);
  const [statusFilter, setStatusFilter] = useState("all");
  const [currencyFilter, setCurrencyFilter] = useState("all");
  const [kpiFilter, setKpiFilter] = useState<string | null>(null);

  const handleKpiClick = (allocationStatus: string) => {
    setKpiFilter((prev) => (prev === allocationStatus ? null : allocationStatus));
  };

  // Sorting
  const [sortField, setSortField]           = useState<SortField | null>(null);
  const [sortDirection, setSortDirection]   = useState<SortDirection>(null);

  // Column interactions
  const [columnWidths, setColumnWidths]     = useState<Record<string, number>>({});
  const [columnOrder, setColumnOrder]       = useState<string[]>(DEFAULT_RECEIPT_COLUMNS.map(c => c.id));
  const [pinnedLeft, setPinnedLeft]         = useState<string[]>([]);
  const [pinnedRight, setPinnedRight]       = useState<string[]>([]);
  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);
  const dragColRef  = useRef<string | null>(null);
  const [dragOverCol, setDragOverCol]       = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage]       = useState(1);
  const [pageSize, setPageSize]             = useState(25);

  const {
    columns: receiptColumns,
    visibleColumns: receiptVisibleColumns,
    savePreferences: saveReceiptColumnPreferences,
    resetToDefaults: resetReceiptColumnPreferences,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.Receipts, DEFAULT_RECEIPT_COLUMNS);
  const columnVisibility = useMemo(() => {
    const vis: Record<string, boolean> = {};
    receiptColumns.forEach((col) => { vis[col.id] = col.visible; });
    return vis;
  }, [receiptColumns]);

  // Build GraphQL params from current filter/sort/page state
  const graphqlParams = useMemo((): GetReceiptsParams => {
    const params: GetReceiptsParams = {
      page: currentPage,
      pageSize,
      sortBy: sortField ?? undefined,
      sortOrder: sortDirection ?? undefined,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    const effectiveStatus = kpiFilter ?? (statusFilter !== "all" ? statusFilter : undefined);
    if (effectiveStatus) params.allocationStatus = effectiveStatus;
    if (currencyFilter !== "all") params.currency = currencyFilter;
    return params;
  }, [currentPage, pageSize, debouncedSearch, statusFilter, currencyFilter, sortField, sortDirection, kpiFilter]);

  // Fetch receipts data using GraphQL (server-side filtering, sorting, pagination)
  const { receipts, pagination: graphqlPagination, totals, isLoading } = useReceiptsPageDataGraphQL(graphqlParams);

  // Get full list of available currencies from API
  const { data: currenciesData = [] } = useCurrenciesList();
  const availableCurrencies = useMemo(
    () => currenciesData.map(x => x.code).sort((a, b) => a.localeCompare(b)),
    [currenciesData],
  );

  // Server-side pagination from GraphQL
  const totalRecords = graphqlPagination.totalItems;
  const totalPages = graphqlPagination.totalPages;

  // KPIs from server-side totals
  const kpis = useMemo(() => ({
    totalReceipts: totals.totalCount,
    totalReceived: totals.totalAmount,
    fullyApplied: totals.fullyAllocatedAmount,
    fullyAppliedCount: totals.fullyAllocatedCount,
    partiallyApplied: totals.partiallyAllocatedAmount,
    partiallyAppliedCount: totals.partiallyAllocatedCount,
    unapplied: totals.unallocatedAmount,
    unappliedCount: totals.unallocatedCount,
  }), [totals]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, currencyFilter, sortField, sortDirection, kpiFilter]);

  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  // Server-side sorted/filtered receipts from GraphQL (already paginated)
  const sortedReceipts = receipts;

  const isFilterActive = !!(searchQuery || statusFilter !== "all" || currencyFilter !== "all" || kpiFilter);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortDirection === "asc")  { setSortDirection("desc"); }
      else                          { setSortField(null); setSortDirection(null); }
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return <span className="text-gray-500"><ArrowUpDown className="h-4 w-4" strokeWidth={2.5} /></span>;
    if (sortDirection === "asc")  return <span className="text-gray-500"><ArrowUp   className="h-4 w-4" strokeWidth={2.5} /></span>;
    return <span className="text-gray-500"><ArrowDown className="h-4 w-4" strokeWidth={2.5} /></span>;
  };

  // Resize handlers
  const startResize = (e: React.MouseEvent, columnId: string) => {
    e.preventDefault();
    const th = (e.target as HTMLElement).closest("th");
    const startWidth = th?.offsetWidth ?? 100;
    resizingRef.current = { columnId, startX: e.clientX, startWidth };
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!resizingRef.current) return;
      const { columnId, startX, startWidth } = resizingRef.current;
      const newWidth = Math.max(60, startWidth + (e.clientX - startX));
      setColumnWidths(prev => ({ ...prev, [columnId]: newWidth }));
    };
    const onMouseUp = () => { resizingRef.current = null; };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    return () => { window.removeEventListener("mousemove", onMouseMove); window.removeEventListener("mouseup", onMouseUp); };
  }, []);

  // Drag handlers
  const handleDragStart = (columnId: string) => { dragColRef.current = columnId; };
  const handleDragOver  = (e: React.DragEvent, columnId: string) => { e.preventDefault(); setDragOverCol(columnId); };
  const handleDrop      = (targetId: string) => {
    const sourceId = dragColRef.current;
    if (!sourceId || sourceId === targetId) { setDragOverCol(null); return; }
    setColumnOrder(prev => {
      const next = [...prev];
      const from = next.indexOf(sourceId);
      const to   = next.indexOf(targetId);
      if (from === -1 || to === -1) return prev;
      next.splice(from, 1);
      next.splice(to, 0, sourceId);
      return next;
    });
    setDragOverCol(null);
    dragColRef.current = null;
  };
  const handleDragEnd = () => { setDragOverCol(null); dragColRef.current = null; };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePageSizeChange = (newSize: string) => {
    const size = parseInt(newSize, 10);
    setPageSize(size);
    setCurrentPage(1);
  };

  const pageNumbers = getPageNumbers(currentPage, totalPages);

  const handleColumnConfigApply = (columns: ColumnDefinition[]) => {
    saveReceiptColumnPreferences(columns);
  };

  // Map frontend column IDs to ReceiptListSort enum names for the export API
  const exportColumnNameMap: Record<string, string> = useMemo(() => ({
    ...sortFieldMap,
    currency: "CurrencyCode",
    customerAccountNumber: "AccountNumber",
    allocatedAmount: "TotalAllocated",
    createdBy: "CreatedByName",
    createdAt: "TransactionCreatedAt",
    modifiedBy: "ModifiedByName",
    modifiedAt: "TransactionModifiedAt",
  }), []);

  // Build columns for server-side export based on visible columns
  const getExportColumns = useCallback((): ColumnInput[] => {
    return receiptVisibleColumns
      .filter((col) => col.id in exportColumnNameMap)
      .map((col) => ({
        name: exportColumnNameMap[col.id],
        displayName: col.label,
      }));
  }, [receiptVisibleColumns, exportColumnNameMap]);

  // Build columns for server-side export with all columns
  const getAllExportColumns = useCallback((): ColumnInput[] => {
    return DEFAULT_RECEIPT_COLUMNS
      .filter((col) => col.id in exportColumnNameMap)
      .map((col) => ({
        name: exportColumnNameMap[col.id],
        displayName: col.label,
      }));
  }, [exportColumnNameMap]);

  // Reuse the same filter builder used by the GraphQL queries
  const getExportFilter = useCallback(
    (): Record<string, unknown> | undefined => buildGraphQLFilterVariable(graphqlParams),
    [graphqlParams],
  );

  // Server-side export hook for current view columns
  const { triggerExportAll: triggerExportCurrentView } = useExportAll({
    form: 'Receipts',
    getColumns: getExportColumns,
    getFilter: getExportFilter,
    fileName: `receipts-export-${new Date().toISOString().split("T")[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Server-side export hook for all columns
  const { triggerExportAll: triggerExportAllColumns } = useExportAll({
    form: 'Receipts',
    getColumns: getAllExportColumns,
    getFilter: getExportFilter,
    fileName: `receipts-all-columns-export-${new Date().toISOString().split("T")[0]}`,
    onFinished: () => toast.success('Export completed successfully'),
  });

  // Handle export to Excel (server-side)
  const handleExportToExcel = () => {
    triggerExportCurrentView();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  // Handle export all columns to Excel (server-side)
  const handleExportAllColumns = () => {
    triggerExportAllColumns();
    toast.info('Export started. You will be notified when the file is ready.');
  };

  const getStatusBadge = (status: AllocationStatus) => {
    switch (status) {
      case "fully_allocated":
        return <Badge className="bg-[#14b8a6] hover:bg-[#0d9488] text-white border-0">Fully Allocated</Badge>;
      case "partially_allocated":
        return <Badge className="bg-amber-500 hover:bg-amber-600 text-white border-0">Partially Allocated</Badge>;
      case "unallocated":
        return <Badge className="bg-red-500 hover:bg-red-600 text-white border-0">Unallocated</Badge>;
      default:
        return <Badge className="bg-muted text-muted-foreground border-0">{status}</Badge>;
    }
  };

  const formatCurrency = (amount: number, currency: string = baseCurrency) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(amount);

  // Column header renderer
  const renderColHeader = (columnId: string) => {
    const cfg        = COL_CONFIG[columnId];
    if (!cfg) return null;
    const isPinnedL  = pinnedLeft.includes(columnId);
    const isPinnedR  = pinnedRight.includes(columnId);
    const width      = columnWidths[columnId];
    const isDragOver = dragOverCol === columnId;

    const label = cfg.sortField ? (
      <button
        className={cn("flex items-center gap-1 hover:text-gray-900 transition-colors", cfg.align === "right" && "justify-end w-full")}
        onClick={() => handleSort(cfg.sortField as SortField)}
      >
        {cfg.label}
        {renderSortIcon(cfg.sortField as SortField)}
      </button>
    ) : (
      <span className={cn(cfg.align === "right" && "block text-right")}>{cfg.label}</span>
    );

    return (
      <th
        key={columnId}
        draggable
        onDragStart={() => handleDragStart(columnId)}
        onDragOver={(e) => handleDragOver(e, columnId)}
        onDrop={() => handleDrop(columnId)}
        onDragEnd={handleDragEnd}
        className={cn(
          "relative h-10 px-4 bg-white font-bold text-gray-700 select-none whitespace-nowrap border-r border-gray-200 last:border-r-0 text-left",
          cfg.align === "right" && "text-right",
          isPinnedL && "sticky left-0 z-20 shadow-[2px_0_4px_rgba(0,0,0,0.06)]",
          isPinnedR && "sticky right-0 z-20 shadow-[-2px_0_4px_rgba(0,0,0,0.06)]",
          isDragOver && "bg-blue-50 border-l-2 border-l-blue-400"
        )}
        style={width ? { width, minWidth: width } : undefined}
      >
        <div className="pr-5">{label}</div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="absolute right-5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600">
              <MoreVertical className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {!isPinnedL && !isPinnedR ? (
              <>
                <DropdownMenuItem onClick={() => { setPinnedLeft(p => [...p, columnId]); setPinnedRight(p => p.filter(x => x !== columnId)); }}>
                  <Pin className="mr-2 h-4 w-4" /> Pin to Left
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setPinnedRight(p => [...p, columnId]); setPinnedLeft(p => p.filter(x => x !== columnId)); }}>
                  <PinOff className="mr-2 h-4 w-4" /> Pin to Right
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem onClick={() => { setPinnedLeft(p => p.filter(x => x !== columnId)); setPinnedRight(p => p.filter(x => x !== columnId)); }}>
                <PinOff className="mr-2 h-4 w-4" /> Unpin Column
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setColumnWidths({})}>
              <RotateCcw className="mr-2 h-4 w-4" /> Reset Column Widths
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setColumnOrder(DEFAULT_RECEIPT_COLUMNS.map(c => c.id))}>
              <RotateCcw className="mr-2 h-4 w-4" /> Reset Column Order
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <div
          className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-blue-400 active:bg-blue-500"
          onMouseDown={(e) => startResize(e, columnId)}
        />
      </th>
    );
  };

  const renderTableBody = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan={columnOrder.filter(id => columnVisibility[id]).length} className="text-center py-8 text-sm text-muted-foreground">
            Loading receipts...
          </td>
        </tr>
      );
    }
    if (sortedReceipts.length === 0) {
      return (
        <tr>
          <td colSpan={columnOrder.filter(id => columnVisibility[id]).length} className="text-center py-8 text-sm text-muted-foreground">
            No receipts found
          </td>
        </tr>
      );
    }
    return sortedReceipts.map((receipt, index) => (
      <tr key={receipt.id} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
        {columnOrder.filter(id => columnVisibility[id]).map(colId => {
          const isPinnedL = pinnedLeft.includes(colId);
          const isPinnedR = pinnedRight.includes(colId);
          const tdClass = cn(
            "px-4 py-3 text-sm border-r border-gray-200 last:border-r-0",
            isPinnedL && "sticky left-0 z-10 bg-inherit shadow-[2px_0_4px_rgba(0,0,0,0.06)]",
            isPinnedR && "sticky right-0 z-10 bg-inherit shadow-[-2px_0_4px_rgba(0,0,0,0.06)]"
          );
          switch (colId) {
            case "receiptNumber":
              return <td key={colId} className={cn(tdClass, "font-medium")}>{receipt.receiptNumber}</td>;
            case "customerAccountNumber":
              return <td key={colId} className={tdClass}>{receipt.customerAccountNumber}</td>;
            case "customerName":
              return (
                <td key={colId} className={tdClass}>
                  <div className="font-medium">
                    {receipt.customerName}
                  </div>
                </td>
              );
            case "currency":
              return <td key={colId} className={tdClass}>{receipt.currency}</td>;
            case "receiptDate":
              return <td key={colId} className={tdClass}>{format(new Date(receipt.receiptDate), "dd/MM/yyyy")}</td>;
            case "receivedAmount":
              return <td key={colId} className={cn(tdClass, "text-right font-medium")}>{formatCurrency(receipt.receivedAmount, receipt.currency)}</td>;
            case "allocatedAmount":
              return <td key={colId} className={cn(tdClass, "text-right")}>{formatCurrency(receipt.allocatedAmount || 0, receipt.currency)}</td>;
            case "unappliedAmount":
              return (
                <td key={colId} className={cn(tdClass, "text-right")}>
                  <span className={cn("font-medium", receipt.unappliedAmount > 0 ? "text-red-500" : "text-muted-foreground")}>
                    {formatCurrency(receipt.unappliedAmount, receipt.currency)}
                  </span>
                </td>
              );
            case "allocationStatus":
              return <td key={colId} className={tdClass}>{getStatusBadge(receipt.allocationStatus)}</td>;
            case "createdBy":
              return <td key={colId} className={tdClass}>{receipt.createdBy || "-"}</td>;
            case "createdAt":
              return <td key={colId} className={tdClass}>{receipt.createdAt ? format(new Date(receipt.createdAt), "dd/MM/yyyy HH:mm") : "-"}</td>;
            case "modifiedBy":
              return <td key={colId} className={tdClass}>{receipt.modifiedBy || "-"}</td>;
            case "modifiedAt":
              return <td key={colId} className={tdClass}>{receipt.modifiedAt ? format(new Date(receipt.modifiedAt), "dd/MM/yyyy HH:mm") : "-"}</td>;
            case "actions":
              return (
                <td key={colId} className={cn(tdClass, "text-right")}>
                  {receipt.allocationStatus !== "fully_allocated" && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); navigate(`/payments/allocate/${receipt.id}`); }}
                      className="gap-1"
                    >
                      <ArrowRightCircle className="h-4 w-4" />
                      Allocate
                    </Button>
                  )}
                </td>
              );
            default:
              return null;
          }
        })}
      </tr>
    ));
  };

  return (
    <div className="flex flex-col h-full overflow-hidden p-4 md:p-6 pt-6 gap-4">

      {/* Page Header */}
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <h2 className="text-3xl font-bold tracking-tight">Receipts</h2>
          <p className="text-muted-foreground">View and manage customer receipts and allocations</p>
        </div>
        <HelpSupportButton screenId="receipts" />
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 shrink-0">
        <ReceiptsKPICard
          title="Total Receipts"
          value={formatCurrency(kpis.totalReceived)}
          subtitle={`${kpis.totalReceipts} receipts`}
          icon={getCurrencyIcon(baseCurrency)}
          colorScheme="blue"
          onClick={() => setKpiFilter(null)}
          isActive={kpiFilter === null}
          infoTooltip={`Total shown in base currency (${baseCurrency})`}
        />
        <ReceiptsKPICard
          title="Fully Allocated"
          value={formatCurrency(kpis.fullyApplied)}
          subtitle={`${kpis.fullyAppliedCount} receipts`}
          icon={CheckCircle2}
          colorScheme="green"
          onClick={() => handleKpiClick("fully_allocated")}
          isActive={kpiFilter === "fully_allocated"}
          infoTooltip={`Total shown in base currency (${baseCurrency})`}
        />
        <ReceiptsKPICard
          title="Partially Allocated"
          value={formatCurrency(kpis.partiallyApplied)}
          subtitle={`${kpis.partiallyAppliedCount} receipts`}
          icon={AlertCircle}
          colorScheme="amber"
          onClick={() => handleKpiClick("partially_allocated")}
          isActive={kpiFilter === "partially_allocated"}
          infoTooltip={`Total shown in base currency (${baseCurrency})`}
        />
        <ReceiptsKPICard
          title="Unallocated"
          value={formatCurrency(kpis.unapplied)}
          subtitle={`${kpis.unappliedCount} receipts`}
          icon={Target}
          colorScheme="red"
          onClick={() => handleKpiClick("unallocated")}
          isActive={kpiFilter === "unallocated"}
          infoTooltip={`Total shown in base currency (${baseCurrency})`}
        />
      </div>

      {/* Table Section */}
      <div className="flex-1 min-h-0 flex flex-col">

        {/* Sticky Toolbar */}
        <div className="sticky top-0 z-20 bg-white border border-b-0 rounded-t-xl px-4 py-3 flex items-center gap-3">
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111827" }} className="whitespace-nowrap">
            {!isFilterActive && "All "}Receipts ({totalRecords})
          </h2>

          {/* Search Bar - fills remaining space */}
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by receipt number, customer..."
              className="pl-8 h-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="fully_allocated">Fully Allocated</SelectItem>
              <SelectItem value="partially_allocated">Partially Allocated</SelectItem>
              <SelectItem value="unallocated">Unallocated</SelectItem>
            </SelectContent>
          </Select>
          <Select value={currencyFilter} onValueChange={setCurrencyFilter}>
            <SelectTrigger className="w-[160px] h-9"><SelectValue placeholder="Currency" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Currencies</SelectItem>
              {availableCurrencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>

          {/* Export and Column Config */}
          <div className="flex items-center gap-2">
            <button onClick={handleExportToExcel} className="text-gray-900 hover:text-gray-600 transition-colors p-1">
              <ArrowDownToLine className="h-5 w-5" />
            </button>
            <ColumnConfigDialog
              columns={receiptColumns}
              onApply={handleColumnConfigApply}
              onReset={resetReceiptColumnPreferences}
              trigger={
                <button className="text-gray-900 hover:text-gray-600 transition-colors p-1">
                  <Settings className="h-5 w-5" />
                </button>
              }
            />
          </div>
        </div>

        {/* Card */}
        <Card className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-t-none border-t-0">
          <CardContent className="flex-1 min-h-0 p-0 overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 overflow-auto">
              <div className="min-w-max">
                <table className="w-full text-sm border-separate border-spacing-0">
                  <thead className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
                    <tr className="h-10 group">
                      {columnOrder.filter(id => columnVisibility[id]).map(id => renderColHeader(id))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {renderTableBody()}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>

          {/* Pagination Footer */}
          {!isLoading && totalRecords > 0 && (
            <div className="shrink-0 flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white pr-16">
              <span className="text-sm text-gray-500">
                Page <span className="font-semibold text-gray-900">{currentPage}</span> of <span className="font-semibold text-gray-900">{totalPages}</span>
              </span>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Rows per page:</span>
                <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {pageNumbers.map((p, i) =>
                  typeof p === "string" ? (
                    <span key={`ellipsis-${i}`} className="px-1 text-gray-400 text-sm">&hellip;</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={cn(
                        "w-8 h-8 rounded text-sm font-medium transition-colors",
                        currentPage === p ? "bg-teal-600 text-white" : "hover:bg-gray-100 text-gray-700"
                      )}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="p-1 rounded hover:bg-gray-100 disabled:opacity-30 transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
